<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { onClickOutside } from '@vueuse/core';
  import { useI18n } from '@/common/composables/vue-i18n.composable';
  import useClusterRegistrationsStore from '@/common/store/pinia/clusterRegistrations';
  import { store } from '@/common/root';
  import ROLES from '@/common/constants/roles.constants';
  import Status from '@/common/shared/Status.vue';
  import { STATUS_ICON_TIMES_O, STATUS_ICON_CHECK_O } from '@/common/utils/statuses';

  const props = withDefaults(
    defineProps<{
      value?: boolean;
      isFullyConfigured?: boolean;
      referenceEl?: Element | null;
    }>(),
    {
      value: false,
      isFullyConfigured: false,
      referenceEl: null,
    }
  );

  const emit = defineEmits<{(e: 'input', value: boolean): void}>();

  const { t } = useI18n();
  const clusterRegistrationsStore = useClusterRegistrationsStore();
  const configStatusPopoverRef = ref<HTMLElement | null>(null);

  const handleClose = () => {
    emit('input', false);
  };

  onClickOutside(configStatusPopoverRef, () => {
    if (props.value) {
      handleClose();
    }
  }, { ignore: ['.header-alerts-config-status-popover'] });

  const popperOptions = computed(() => ({
    gpuAcceleration: false,
    modifiers: [
      (data: Record<string, any>) => {
        const linkEl = props.referenceEl?.querySelector('.el-ribbon__description .el-link') ?? props.referenceEl;
        if (linkEl) {
          const rect = linkEl.getBoundingClientRect();

          data.offsets.reference = {
            top: rect.top,
            left: rect.left,
            right: rect.right,
            bottom: rect.bottom,
            width: rect.width,
            height: rect.height,
          };
        }
        return data;
      },
      'shift', 'offset', 'preventOverflow', 'keepTogether', 'arrow', 'flip', 'applyStyle',
    ],
  }));

  const configStatusItems = computed(() => {
    const isComputeDeployed = store.getters['compute/isComputeConfigured'];
    const computeFeatures = store.getters['compute/computeClusterFeatures'] || {};
    const isBillingMeteringEnabled = !!computeFeatures.metering;
    const isSelfServiceEnabled = !!store.getters['rolesets/getRolesSetByRoleName']?.(ROLES.SELF_SERVICE);
    const hasSspUrl = !!clusterRegistrationsStore.config?.ssp_url;

    return [
      { label: t('Deploy the compute service'), done: isComputeDeployed, to: '/compute' },
      { label: t('Install the billing metering compute add-on'), done: isBillingMeteringEnabled, to: '/compute/settings/addons' },
      { label: t('Enable self-service'), done: isSelfServiceEnabled, to: '/settings/system/self-service' },
      { label: t('Add a public self-service URL'), done: hasSspUrl, to: '/settings/cluster-registration/add-ssp-url' }
    ];
  });

  defineExpose({ configStatusItems, handleClose });
</script>

<template>
<div data-component-name="config-status-popover"
  v-if="!isFullyConfigured"
  ref="configStatusPopoverRef"
>
  <el-popover data-component-name="el-popover"
    :value="value"
    :popper-options="popperOptions"
    trigger="manual"
    placement="bottom-start"
    width="440"
    popper-class="header-alerts-config-status-popover"
    @input="emit('input', $event)"
  >
    <template #reference>
      <span />
    </template>
    <div class="px-24 py-16">
      <p class="ma-0 pa-0">
        {{ $t('To complete the Cyber Frame deployment, perform the following steps:') }}
      </p>
      <ul class="config-status-popover__list ma-0 pa-0">
        <li
          v-for="(item, index) in configStatusItems"
          :key="index"
          class="el-text--strong pt-12"
          @click="handleClose"
        >
          <router-link data-component-name="router-link"
            v-if="!item.done"
            :to="item.to"
          >
            <Status data-component-name="status" :status="STATUS_ICON_TIMES_O">
              {{ item.label }}
            </Status>
          </router-link>
          <span
            v-else
            class="config-status-popover__item-done"
          >
            <Status data-component-name="status" :status="STATUS_ICON_CHECK_O">
              {{ item.label }}
            </Status>
          </span>
        </li>
      </ul>
    </div>
  </el-popover>
</div>
</template>

<style scoped lang="scss">
  .config-status-popover__list {
    list-style: none;
  }

  .config-status-popover__item-done ::v-deep .status-text {
    text-decoration: line-through;
  }
</style>

<style lang="scss">
  .el-popper.header-alerts-config-status-popover {
    margin-top: 32px;
  }
</style>
